import React, { Component, ReactNode } from 'react';
import './ChatBalloon.scss';
import { ChatMessage } from '../../models/ChatMessage';
import urlMetadata from 'url-metadata';
import { ChatUrlInfo } from './ChatUrlInfo';
import { connect } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { SingleUser } from '../../api/types';
import { Document, EDocumentType } from '../../models/Document';
import { DocumentHelper } from '../../helpers/DocumentHelper';
import { Api } from '../../api';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import Button from 'react-bootstrap/Button';

interface ChatBalloonProps {
  user?: SingleUser;
  chatMessage: ChatMessage;
  doNotDisplayUserNames?: boolean;
  hideTime?: boolean;
  dossierId: number;
  className?: string;
}

interface ChatBalloonState {
  links: LinkData[];
}

interface LinkData {
  url: string;
  og?: {
    baseUrl: string;
    title?: string;
    image?: string;
  }
}

class ChatBalloonComponent extends Component<ChatBalloonProps, ChatBalloonState> {
  // Got this bad boy from https://urlregex.com/
  private static readonly URL_REGEX = /((?:(?:[A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www\.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)(?:(?:\/[+~%/.\w\-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[.!/\\\w]*))?)/gi;
  // Base64 regex derived from https://www.ietf.org/rfc/rfc4648.txt
  private static readonly TOPP_FILE_REGEX = /((?:topp-file\/\/)(?:(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?))/g;
  private static readonly TOPP_FILE_REGEX2 = /(topp-file\/\/)(([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?)/g;
  private static readonly URL_BASE_REGEX = /^.+?[^/:](?=[?/]|$)/i;

  constructor(props: ChatBalloonProps) {
    super(props);
    const { chatMessage } = props;
    this.state = {
      links: this.getLinks(chatMessage)
    };
    /*
    // Disabled: awaiting further implementation
    this.enrichLinks()
      .catch(err => console.error('Failed to enrich links', err));
    */
  }

  private getLinks(chatMessage: ChatMessage): LinkData[] {
    const regex = new RegExp(ChatBalloonComponent.URL_REGEX);
    const urls = chatMessage.message.match(regex);
    if (urls) {
      return urls.map(url => ({ url }));
    }
    return [];
  }

  private async enrichLinks() {
    const { links } = this.state;
    if (links.length === 0) {
      return;
    }

    // https://cors-anywhere.herokuapp.com is used for dev testing,
    // but we need a backend (microservice) to get this data.
    // eslint-disable-next-line no-console
    console.log(`
        If you are seeing this message it means
        that 'https://cors-anywhere.herokuapp.com/' is still being
        used, remove this before going live!
      `);

    const data: LinkData[] = [];
    for (const link of links) {
      const { url } = link;
      const urlData: LinkData = { url };
      try {
        const result = await urlMetadata(`https://cors-anywhere.herokuapp.com/${url}`);
        urlData.og = {
          baseUrl: this.getBaseURL(url),
          title: result.title,
          image: result.image
        };
      } catch (error) {
        console.warn('Failed to get url metadata for', url, error);
      }
      data.push(urlData);
    }
    this.setState({ links: data });
  }

  private getBaseURL(fullUrl: string): string {
    const regex = new RegExp(ChatBalloonComponent.URL_BASE_REGEX);
    const result = fullUrl.match(regex);
    if (result && result.length !== 0) {
      return result[0];
    } else {
      throw new Error('Failed to get base url from full url.');
    }
  }

  private renderText(message: string): JSX.Element[] {
    const regex = new RegExp(`${ChatBalloonComponent.TOPP_FILE_REGEX.source}|${ChatBalloonComponent.URL_REGEX.source}`, 'g');
    const parts: string[] = message.split(regex);
    const elements: JSX.Element[] = Array<JSX.Element>(parts.length);
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;
      if (part.match(ChatBalloonComponent.URL_REGEX)) {
        elements[i] = (<a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>);
      } else if (part.match(ChatBalloonComponent.TOPP_FILE_REGEX2)) {
        const result = ChatBalloonComponent.TOPP_FILE_REGEX2.exec(part);
        let doc = new Document({ lastModified: '', name: 'Invalid file', bytes: 0 });
        let docType: EDocumentType;
        let memberId: string;
        if (result && result.length > 2) {
          const fileInfo = atob(result[2]);
          const [name, type, id] = fileInfo.split(':');
          memberId = id;
          doc = new Document({ lastModified: '', name, bytes: 0 });
          docType = type as EDocumentType;
        }
        elements[i] = (
          <Button
            key={i}
            variant="link"
            onClick={() => {
              DocumentHelper.openDocumentInWindow(doc, docType, (document, documentType) => this.handleDownloadDocument(document, documentType, parseInt(memberId)));
            }}
          >{doc.name}</Button>
        );
      } else {
        elements[i] = (<span key={i}>{part}</span>);
      }
    }
    return elements;
  }

  private handleDownloadDocument = (document: Document, type: EDocumentType, memberId?: number): Promise<Blob> => {
    const { dossierId } = this.props;
    if (dossierId) {
      let apiCall = type === EDocumentType.GLOBAL ? Api.getGlobalDossierDocument(document) : Api.getSharedDossierDocument(dossierId, document);
      switch (type) {
        case EDocumentType.GLOBAL:
          apiCall = Api.getGlobalDossierDocument(document);
          break;
        case EDocumentType.SHARED:
          apiCall = Api.getSharedDossierDocument(dossierId, document);
          break;
        case EDocumentType.FILE:
          apiCall = Api.getDossierDocument(dossierId, document);
          break;
        case EDocumentType.RESOURCE:
          if (!memberId) {
            throw new Error('Something went wrong');
          }
          apiCall = Api.getResourceDocument(dossierId, memberId, document);
          break;
        default:
          throw new Error('Something went wrong');
      }
      return apiCall
        .catch(err => {
          NotificationHelper.toastError('Openen bestand mislukt, probeer het later opnieuw.');
          throw err;
        });
    } else {
      NotificationHelper.toastError('Openen bestand mislukt, omdat er geen dossier geselecteerd is.');
      return Promise.reject(new Error('Dossier not selected'));
    }
  };

  render(): ReactNode {
    const { chatMessage, className, doNotDisplayUserNames, hideTime, user } = this.props;
    const { links } = this.state;
    const fromThisUser = chatMessage.sendByName === user?.name;
    let fullClassName = `chat-balloon chat-balloon--${fromThisUser ? 'sent' : 'received'}`;
    if (className) {
      fullClassName += ` ${className}`;
    }
    const messageTime = chatMessage.timestamp.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    return (
      <div className={fullClassName}>
        <div className="chat-balloon__container">
          {
            (!fromThisUser && !doNotDisplayUserNames) && <div className="chat-balloon__sender">{chatMessage.sendByName}</div>
          }
          <div className={'chat-balloon__message'}>{this.renderText(chatMessage.message)}</div>
          {
            links.map((linkData: LinkData, index: number) => {
              let imageUrl;
              if (linkData.og?.baseUrl && linkData.og.image) {
                imageUrl = `${linkData.og.baseUrl}${linkData.og.image}`;
              }
              return (
                <ChatUrlInfo
                  className="chat-balloon__link"
                  key={index}
                  url={linkData.url}
                  title={linkData.og?.title}
                  imageUrl={imageUrl}
                />
              );
            })
          }
        </div>
        {
          !hideTime &&
          <div className="chat-balloon__time">
            {messageTime}
            <div className={(chatMessage.read ? 'chat-balloon__time-read' : '')}> </div>
          </div>
        }
      </div>
    );
  }
}

export const ChatBalloon = connect((state: ApplicationState) => ({
  user: state.session.user,
}))(ChatBalloonComponent);
